import { handleError } from './handleError';
import { AuthKeys, FetchDataResponse } from './types';
import { isBinaryBody } from './helpers/common';

type MethodType = 'GET' | 'POST' | 'DELETE';
type HeadersType = Record<string, string>;

export const fetchData = async (
	baseUrl: string,
	method: MethodType,
	body: any = null,
	authKeys: AuthKeys = {},
	headersCustom: HeadersType = {},
): Promise<FetchDataResponse> => {
	const headers: HeadersType = {
		...headersCustom,
	};

	const isJson =
		body &&
		typeof body === 'object' &&
		!isBinaryBody(body) &&
		!(body instanceof FormData);

	if (isJson) {
		headers['Content-Type'] = 'application/json';
		headers['Accept'] = 'application/json';
	}

	if (isBinaryBody(body)) {
		headers['Content-Type'] = 'application/octet-stream';
	}

	if (authKeys?.apiKey) {
		headers['x-api-key'] = authKeys.apiKey;
	}

	if (authKeys?.token) {
		headers['x-token'] = authKeys.token;
	}

	const isStream = headers['x-bs-stream'] === '1';

	return performFetchRequest(baseUrl, method, isJson, body, headers, isStream);
};

const performFetchRequest = async (
	baseUrl: string,
	method: MethodType,
	isJson: boolean,
	body: any = null,
	headers: HeadersType = {},
	isStream: boolean = false,
): Promise<FetchDataResponse> => {
	const res = await fetch(baseUrl, {
		method,
		headers,
		body: body ? (isJson ? JSON.stringify(body) : body) : null,
		cache: 'no-store',
	});

	if (!res.ok) {
		return handleError(res);
	}

	if (res.status === 204 || res.status === 205) {
		return { data: null };
	}

	const contentType = res.headers.get('content-type') || '';

	if (isStream) {
		return {
			data: res.body as any,
			contentType,
			contentDisposition: res.headers.get('content-disposition'),
		};
	}

	if (contentType.includes('application/json')) {
		const text = await res.text();

		if (!text) {
			return { data: null };
		}

		try {
			return { data: JSON.parse(text) };
		} catch {
			return { data: text };
		}
	}

	const blob = await res.blob();
	return { data: blob };
};
